import { useRef, useState } from "react";
import { Upload, Camera, Loader2, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errMsg, reportUpload, RECEIPT_ACCEPT } from "../lib/api";

export const ReceiptUploadButtons = ({ transactionId, onDone, label = "Beleg hinzufügen", multiple, testPrefix = "receipt" }) => {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files, source) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await api.uploadReceipts(files, transactionId, source);
      reportUpload(toast, res);
      if (transactionId) {
        res.duplicates.forEach((d) => toast("Vorhandenen Beleg verknüpfen?", {
          action: { label: "Verknüpfen", onClick: async () => { await api.linkReceipt(d.existing_receipt_id, transactionId); onDone(); } },
        }));
      }
      onDone(res);
    } catch (e) { toast.error("Upload fehlgeschlagen", { description: errMsg(e) }); }
    finally { setBusy(false); }
  };
  const onPick = (src) => (e) => { const f = Array.from(e.target.files || []); e.target.value = ""; upload(f, src); };
  return (
    <div className="flex flex-wrap gap-2">
      <input ref={fileRef} type="file" multiple={multiple} accept={RECEIPT_ACCEPT} className="hidden" onChange={onPick("inbox")} data-testid={`${testPrefix}-file-input`} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick("camera")} data-testid={`${testPrefix}-camera-input`} />
      <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testPrefix}-upload-btn`} className="h-10 gap-1.5 bg-slate-900 hover:bg-slate-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
      </Button>
      <Button variant="outline" onClick={() => camRef.current?.click()} disabled={busy} data-testid={`${testPrefix}-camera-btn`} className="h-10 gap-1.5 lg:hidden">
        <Camera className="h-4 w-4" /> Foto aufnehmen
      </Button>
    </div>
  );
};

export const Dropzone = ({ onDone }) => {
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const drop = async (e) => {
    e.preventDefault();
    setOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    setBusy(true);
    try { reportUpload(toast, await api.uploadReceipts(files)); onDone(); }
    catch (err) { toast.error("Upload fehlgeschlagen", { description: errMsg(err) }); }
    finally { setBusy(false); }
  };
  return (
    <div
      data-testid="receipt-dropzone"
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      className={`rounded-xl border-2 border-dashed p-6 transition-colors duration-150 sm:p-8 ${over ? "border-slate-900 bg-amber-50" : "border-slate-300 bg-white"}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          {busy ? <Loader2 className="mt-0.5 h-6 w-6 animate-spin text-slate-500" /> : <InboxIcon className="mt-0.5 h-6 w-6 text-slate-400" />}
          <div>
            <div className="font-semibold text-slate-900">Belege hierher ziehen</div>
            <div className="text-sm text-slate-500">Mehrere PDF, JPG, PNG oder HEIC auf einmal. Die KI liest jeden Beleg und schlägt passende Buchungen vor.</div>
          </div>
        </div>
        <ReceiptUploadButtons onDone={onDone} label="Dateien wählen" multiple testPrefix="inbox" />
      </div>
    </div>
  );
};
